import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, ImageIcon, LogIn, Pencil, Plus, Save, Trash2, Upload, UserRound } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { Modal } from "../../components/ui/Modal";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { useBrand } from "../../components/brand/BrandProvider";
import { IMPERSONATE_RETURN_KEY } from "../../components/layout/TopBar";
import { useSession } from "../../hooks/useSession";
import { brandApi } from "../../api/brandApi";
import { managersAdminApi, type ManagerAdmin, type ManagerLogin } from "../../api/managersAdminApi";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_COLOR = "#00C300";

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "0.9rem",
  fontFamily: "inherit",
  background: "white",
};
const fieldGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 };
const listRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "12px 2px", borderTop: "1px solid var(--portal-border)" };

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>{children}</span>;
}

function Status({ error, success }: { error: string | null; success: string | null }) {
  if (!error && !success) return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", color: error ? "#c0392b" : "var(--portal-status-success-darker)", fontSize: "0.85rem" }}>
      {error ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
      <span>{error ?? success}</span>
    </div>
  );
}

/**
 * Admin page for one manager (whitelabel brand): details and mail sender,
 * brand colour and logo, and the manager's portal logins.
 */
export function ManagerEditPage() {
  const { managerId: param } = useParams<{ managerId: string }>();
  const managerId = Number(param);
  const [manager, setManager] = useState<ManagerAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setManager(await managersAdminApi.get(managerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load manager");
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId]);

  return (
    <>
      <PageHeader
        title={manager ? manager.name : "Manager"}
        actions={
          <Link to="/admin/managers" className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
            <ArrowLeft size={14} /> Back to managers
          </Link>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        {error && <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>{error}</div>}
        {!manager && !error && <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>}
        {manager && (
          <>
            <DetailsSection manager={manager} onSaved={setManager} />
            <BrandSection manager={manager} onSaved={load} />
            <LoginsSection managerId={manager.managerId} />
          </>
        )}
      </div>
    </>
  );
}

function DetailsSection({ manager, onSaved }: { manager: ManagerAdmin; onSaved: (m: ManagerAdmin) => void }) {
  const [name, setName] = useState(manager.name);
  const [portalName, setPortalName] = useState(manager.portalName);
  const [portalUrl, setPortalUrl] = useState(manager.portalUrl);
  const [mailFrom, setMailFrom] = useState(manager.mailFrom);
  const [mailFromName, setMailFromName] = useState(manager.mailFromName);
  const [footerHtml, setFooterHtml] = useState(manager.footerHtml);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { refresh: refreshBrand } = useBrand();

  const save = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const m = await managersAdminApi.update(manager.managerId, { name, portalName, portalUrl, mailFrom, mailFromName, footerHtml });
      onSaved(m);
      setSuccess("Saved");
      refreshBrand().catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3 style={{ margin: 0 }}>Details</h3></header>
      <div style={fieldGrid}>
        <label style={{ display: "grid", gap: 4 }}><FieldLabel>Name</FieldLabel><input className="portal-input-inline" style={inputStyle} value={name} maxLength={50} onChange={(e) => setName(e.target.value)} /></label>
        <label style={{ display: "grid", gap: 4 }}><FieldLabel>Portal name (header and e-mails)</FieldLabel><input className="portal-input-inline" style={inputStyle} value={portalName} maxLength={100} onChange={(e) => setPortalName(e.target.value)} placeholder="e.g. Voice Portal" /></label>
        <label style={{ display: "grid", gap: 4 }}><FieldLabel>Portal URL (host, used in e-mail links)</FieldLabel><input className="portal-input-inline" style={inputStyle} value={portalUrl} maxLength={100} onChange={(e) => setPortalUrl(e.target.value)} placeholder="portal.example.com" /></label>
        <label style={{ display: "grid", gap: 4 }}><FieldLabel>Mail from address</FieldLabel><input className="portal-input-inline" style={inputStyle} value={mailFrom} maxLength={150} onChange={(e) => setMailFrom(e.target.value)} placeholder="no-reply@systematix.solutions (default)" /></label>
        <label style={{ display: "grid", gap: 4 }}><FieldLabel>Mail from name</FieldLabel><input className="portal-input-inline" style={inputStyle} value={mailFromName} maxLength={150} onChange={(e) => setMailFromName(e.target.value)} placeholder={`${name || "Manager"} - ${portalName || "Portal"} (default)`} /></label>
      </div>
      <label style={{ display: "grid", gap: 4 }}>
        <FieldLabel>E-mail footer (HTML, optional; default "© year portal")</FieldLabel>
        <textarea className="portal-input-inline" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={footerHtml} maxLength={1000} onChange={(e) => setFooterHtml(e.target.value)} />
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={14} /> {busy ? "Saving…" : "Save"}
        </button>
        <Status error={error} success={success} />
      </div>
    </section>
  );
}

function BrandSection({ manager, onSaved }: { manager: ManagerAdmin; onSaved: () => Promise<void> }) {
  const current = manager.primaryColor ?? DEFAULT_COLOR;
  const [color, setColor] = useState(current);
  const [hexInput, setHexInput] = useState(current);
  const [savingColor, setSavingColor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { refresh: refreshBrand } = useBrand();

  useEffect(() => {
    setColor(current);
    setHexInput(current);
  }, [current]);

  const saveColor = async () => {
    if (!HEX_RE.test(hexInput)) {
      setError("Enter a 6-digit hex colour like #00C300");
      return;
    }
    setSavingColor(true);
    setError(null);
    try {
      await brandApi.setPrimaryColor(manager.managerId, hexInput.toUpperCase());
      setSuccess("Colour saved");
      await onSaved();
      refreshBrand().catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save colour");
    } finally {
      setSavingColor(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await brandApi.uploadLogo(manager.managerId, file);
      setSuccess("Logo uploaded");
      setLogoVersion((v) => v + 1);
      await onSaved();
      refreshBrand().catch(() => undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const logoSrc = manager.hasLogo ? `/api/brand/logo?managerId=${manager.managerId}&v=${logoVersion}` : null;

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3 style={{ margin: 0 }}>Brand</h3></header>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ width: 120, height: 72, border: "1px solid var(--portal-border)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "white", overflow: "hidden" }}>
          {logoSrc ? <img src={logoSrc} alt={`${manager.name} logo`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : <ImageIcon size={24} color="var(--portal-muted)" />}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={handleFile} disabled={uploading} style={{ display: "none" }} />
          <button type="button" className="btn btn-secondary" onClick={() => fileRef.current?.click()} disabled={uploading} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload logo"}
          </button>
          <div style={{ color: "var(--portal-muted)", fontSize: "0.75rem", marginTop: 4 }}>PNG, SVG, JPEG or WebP. Max 1 MB. Without a logo the Systematix logo is shown.</div>
        </div>
      </div>
      <div style={{ display: "grid", gap: 4, maxWidth: 420 }}>
        <FieldLabel>Primary colour</FieldLabel>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="color" value={HEX_RE.test(color) ? color : DEFAULT_COLOR} onChange={(e) => { setColor(e.target.value); setHexInput(e.target.value.toUpperCase()); }} disabled={savingColor} style={{ width: 40, height: 36, padding: 0, border: "1px solid var(--portal-border)", borderRadius: 6 }} aria-label="Pick primary colour" />
          <input type="text" className="portal-input-inline" value={hexInput} onChange={(e) => { setHexInput(e.target.value); if (HEX_RE.test(e.target.value)) setColor(e.target.value); }} disabled={savingColor} spellCheck={false} placeholder="#RRGGBB" style={{ ...inputStyle, fontFamily: "monospace" }} />
          <button type="button" className="btn btn-primary" onClick={() => void saveColor()} disabled={savingColor || hexInput.toUpperCase() === current.toUpperCase()} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Save size={14} /> {savingColor ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <Status error={error} success={success} />
    </section>
  );
}

function LoginsSection({ managerId }: { managerId: number }) {
  const [rows, setRows] = useState<ManagerLogin[] | null>(null);
  const [editing, setEditing] = useState<ManagerLogin | "new" | null>(null);
  const [removePending, setRemovePending] = useState<ManagerLogin | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useSession();
  const { refresh: refreshBrand } = useBrand();

  // Same flow as "Switch to" on the customers list: the server re-authenticates the
  // session as the manager login and remembers the admin, TopBar shows "Switch back".
  const impersonate = async (login: ManagerLogin) => {
    setError(null);
    setSwitching(login.loginId);
    try {
      sessionStorage.setItem(IMPERSONATE_RETURN_KEY, location.pathname + location.search);
    } catch {
      // storage unavailable
    }
    try {
      const next = await managersAdminApi.switchToLogin(managerId, login.loginId);
      setSession(next?.isAuthenticated ? next : null);
      try {
        await refreshBrand();
      } catch {
        // cosmetic
      }
      navigate("/admin/customers");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign in as this manager");
      setSwitching(null);
    }
  };

  useEffect(() => {
    let active = true;
    managersAdminApi.logins(managerId).then((r) => active && setRows(r)).catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load logins"));
    return () => {
      active = false;
    };
  }, [managerId]);

  const remove = async (loginId: number) => {
    setBusy(true);
    setError(null);
    try {
      setRows(await managersAdminApi.deleteLogin(managerId, loginId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete login");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Manager logins</h3>
        <button type="button" className="btn btn-primary" onClick={() => setEditing("new")} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
          <Plus size={14} /> Add login
        </button>
      </header>
      <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>These logins sign in to the manager portal and see this manager's customers.</p>
      {error && <Status error={error} success={null} />}
      {rows == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No logins.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rows.map((l) => (
            <div key={l.loginId} style={listRow}>
              <UserRound size={16} style={{ color: "var(--portal-muted)", flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.username}>{l.username || "—"}</span>
              <button type="button" className="btn btn-secondary" onClick={() => impersonate(l)} disabled={switching != null} title="Sign in as this manager login" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}>
                <LogIn size={14} /> {switching === l.loginId ? "Switching…" : "Impersonate"}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(l)} title="Edit login" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}>
                <Pencil size={14} /> Edit
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setRemovePending(l)} title="Delete login" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem", flexShrink: 0 }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ManagerLoginModal
          managerId={managerId}
          value={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setRows(next);
            setEditing(null);
          }}
        />
      )}

      <ConfirmModal
        open={removePending != null}
        onClose={() => !busy && setRemovePending(null)}
        title="Delete login?"
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          const l = removePending;
          setRemovePending(null);
          if (l) void remove(l.loginId);
        }}
      >
        Delete login <strong>{removePending?.username}</strong>? This cannot be undone.
      </ConfirmModal>
    </section>
  );
}

function ManagerLoginModal({ managerId, value, onClose, onSaved }: { managerId: number; value: ManagerLogin | null; onClose: () => void; onSaved: (rows: ManagerLogin[]) => void }) {
  const isNew = value == null;
  const [username, setUsername] = useState(value?.username ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!username.trim()) return setError("Username is required.");
    if (isNew && !password) return setError("A password is required for a new login.");
    setError(null);
    setBusy(true);
    try {
      onSaved(await managersAdminApi.upsertLogin(managerId, { loginId: value?.loginId ?? 0, username: username.trim(), password }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save login");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      title={isNew ? "Add manager login" : "Edit manager login"}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && <div style={{ color: "#c0392b", fontSize: "0.9rem" }}>{error}</div>}
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>Username</FieldLabel>
          <input type="text" value={username} maxLength={50} autoComplete="off" onChange={(e) => setUsername(e.target.value)} className="portal-input-inline" style={inputStyle} />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <FieldLabel>{isNew ? "Password" : "New password (leave blank to keep current)"}</FieldLabel>
          <input type="text" value={password} maxLength={50} autoComplete="new-password" onChange={(e) => setPassword(e.target.value)} className="portal-input-inline" style={inputStyle} />
        </label>
      </div>
    </Modal>
  );
}
